/**
 * What the bridge drain collects from IndexedDB (migration plan §6.2 step 2(c)).
 *
 * READ ONLY. Nothing in this file writes, updates or deletes a row: the drain
 * copies pending records into the handoff, and the records leave the device
 * only through their own sync path (which deletes or flags them once the
 * server has them) or the MAUI import. drainNeverDeletes.contract.spec.ts
 * pins that.
 *
 * The is_synced pitfall (offline/__tests__/is-synced-indexing.spec.ts):
 * IndexedDB cannot index booleans, so a row written `is_synced: false` is not
 * in the is_synced index and `.where('is_synced').equals(0)` never returns it.
 * Some tables here are written with booleans, some with 0/1. So pending rows
 * are found by a FULL SCAN with isPendingRow(), which treats everything that
 * is not strictly synced (true / 1) as pending: false, 0, undefined, null.
 * A record wrongly treated as pending costs a few bytes in the handoff; one
 * wrongly treated as synced is lost when Play replaces the app.
 */
import type { Table } from 'dexie';
import type { HandoffSource } from './drain-types';

export type DrainTableName =
  | 'pendingMutations'
  | 'offlinePhotos'
  | 'offlineCheckins'
  | 'offlineSupplyRequests'
  | 'offlineForms'
  | 'formDrafts'
  | 'offlineBreadcrumbs'
  | 'personnelPositionBuffer'
  | 'offlineActivationLogs'
  | 'cachedDamageAssessments'
  | 'cachedTasks'
  | 'cachedRoadClosures'
  | 'cachedUtilityOutages'
  | 'cachedActivityEntries'
  | 'cachedRosterEntries'
  | 'cachedBoardPosts'
  | 'cachedIncidents'
  | 'cachedCriticalInfrastructure'
  | 'cachedFemaCosts'
  | 'cachedUnitLogs'
  | 'cachedSupplyRequests'
  | 'offlineSnapshots'
  | 'ics214RosterPending';

export interface DrainTableSpec {
  table: DrainTableName;
  source: HandoffSource;
  /**
   * queue: every row is pending (rows are deleted on delivery).
   * flag: pending = not strictly synced (is_synced not true / 1).
   * syncedAt: pending = no synced_at yet (rows are kept and stamped on delivery).
   */
  mode: 'queue' | 'flag' | 'syncedAt';
  /** Rows carry binary blobs (photos, attachments): handed off one file per blob. */
  binary?: boolean;
  /** Positions: handed off in chunks of <= 500 together with the plugin store's fixes. */
  positions?: boolean;
  /** Local working state (drafts): handed off, never server-copied as "undeliverable". */
  draft?: boolean;
}

/** §6.2 step 2(c), in this order. */
export const DRAIN_TABLES: readonly DrainTableSpec[] = [
  { table: 'pendingMutations', source: 'pending_mutations', mode: 'queue', binary: true },
  { table: 'offlinePhotos', source: 'offline_photos', mode: 'flag', binary: true },
  { table: 'offlineCheckins', source: 'offline_checkins', mode: 'flag' },
  { table: 'offlineSupplyRequests', source: 'offline_supply_requests', mode: 'flag' },
  { table: 'offlineForms', source: 'offline_forms', mode: 'flag' },
  { table: 'formDrafts', source: 'form_drafts', mode: 'flag', draft: true },
  { table: 'offlineBreadcrumbs', source: 'offline_breadcrumbs', mode: 'flag' },
  { table: 'personnelPositionBuffer', source: 'personnel_position_buffer', mode: 'flag', positions: true },
  { table: 'offlineActivationLogs', source: 'offline_activation_logs', mode: 'flag' },
  { table: 'cachedDamageAssessments', source: 'cached_damage_assessments', mode: 'flag' },
  { table: 'cachedTasks', source: 'cached_tasks', mode: 'flag' },
  { table: 'cachedRoadClosures', source: 'cached_road_closures', mode: 'flag' },
  { table: 'cachedUtilityOutages', source: 'cached_utility_outages', mode: 'flag' },
  { table: 'cachedActivityEntries', source: 'cached_activity_entries', mode: 'flag' },
  { table: 'cachedRosterEntries', source: 'cached_roster_entries', mode: 'flag' },
  // Written unsynced on the bridge too (board posts, offline incidents, critical
  // infrastructure edits, FEMA costs, ICS-214 unit logs kept local, supply-request
  // drafts, sUAS snapshots, the ICS-214 roster queue): the WebView store is deleted
  // after the MAUI import, so each is drained like the others.
  { table: 'cachedBoardPosts', source: 'cached_board_posts', mode: 'flag' },
  { table: 'cachedIncidents', source: 'cached_incidents', mode: 'flag' },
  { table: 'cachedCriticalInfrastructure', source: 'cached_critical_infrastructure', mode: 'flag' },
  { table: 'cachedFemaCosts', source: 'cached_fema_costs', mode: 'flag' },
  { table: 'cachedUnitLogs', source: 'cached_unit_logs', mode: 'flag' },
  { table: 'cachedSupplyRequests', source: 'cached_supply_requests', mode: 'flag' },
  { table: 'offlineSnapshots', source: 'offline_snapshots', mode: 'flag' },
  { table: 'ics214RosterPending', source: 'ics214_roster_pending', mode: 'syncedAt' },
];

/** Tables the drain never needs: the safety outbox has its own lane (it is drained first, separately). */
export const DRAIN_EXEMPT_TABLES: ReadonlySet<string> = new Set(['safetyOutbox']);

/**
 * A row of a table OUTSIDE the drain list that the bridge still owes the
 * server: it carries a sync flag that is not strictly synced, or pending
 * local changes. Rows with no sync flag at all are server caches.
 */
export function isUndrainedPendingRow(row: Record<string, unknown> | null | undefined): boolean {
  if (!row || typeof row !== 'object') return false;
  if (row.pending_changes === true) return true;
  if ('is_synced' in row) return isPendingRow(row as { is_synced?: unknown });
  // Older builds wrote camelCase (offlineGpsTracks / offlineGpsTrackPoints).
  if ('isSynced' in row) return isPendingRow({ is_synced: row.isSynced });
  return false;
}

/** A Dexie-like database that can list all its tables. */
export interface ListableDb {
  tables?: ReadonlyArray<{ name: string; toArray(): Promise<unknown[]> }>;
}

/**
 * The guard behind the receipt: every table that is NOT drained, scanned for a
 * row the bridge still owes ("<table>:<count>" each). Anything here means the
 * handoff would not hold everything, so no receipt may be written. A table that
 * cannot be read is reported too ("<table>:unreadable").
 */
export async function collectUndrainedPending(dbLike: ListableDb): Promise<string[]> {
  const drained = new Set<string>(DRAIN_TABLES.map((t) => t.table));
  const out: string[] = [];
  for (const table of dbLike.tables ?? []) {
    if (drained.has(table.name) || DRAIN_EXEMPT_TABLES.has(table.name)) continue;
    let rows: unknown[];
    try {
      rows = await table.toArray();
    } catch {
      out.push(`${table.name}:unreadable`);
      continue;
    }
    const n = rows.filter((r) => isUndrainedPendingRow(r as Record<string, unknown>)).length;
    if (n > 0) out.push(`${table.name}:${n}`);
  }
  return out;
}

/** Strictly synced is true or 1. Everything else (false, 0, undefined, null, '0') is pending. */
export function isPendingRow(row: { is_synced?: unknown } | null | undefined): boolean {
  if (!row) return false;
  return row.is_synced !== true && row.is_synced !== 1;
}

/** A row its own sync path gave up on for good (permanent 4xx): server-copy it now. */
export function isPermanentlyRefused(row: Record<string, unknown>): boolean {
  return row.dead_letter === true || row.status === 'failed';
}

/** A Dexie-like table: only full-scan reads are used, never an is_synced index. */
export interface ReadableTable {
  name: string;
  schema: { primKey: { keyPath: string | string[] | null | undefined } };
  toArray(): Promise<Array<Record<string, unknown>>>;
}

export type ReadableDb = Record<DrainTableName, ReadableTable | Table<unknown, unknown> | undefined>;

export interface PendingRow {
  spec: DrainTableSpec;
  /** `${table}:${primary key}`: the local identity of the record. */
  key: string;
  primaryKey: string;
  row: Record<string, unknown>;
}

function primaryKeyOf(table: ReadableTable, row: Record<string, unknown>): string | null {
  const path = table.schema?.primKey?.keyPath;
  if (typeof path === 'string' && path.length > 0) {
    const v = row[path];
    return v === undefined || v === null ? null : String(v);
  }
  if (Array.isArray(path)) return path.map((p) => String(row[p])).join('|');
  return null;
}

/**
 * Every pending row of every drain table, by full scan. A table that is
 * missing (an older schema) or fails to read is reported, never skipped
 * silently.
 */
export async function collectPendingRows(dbLike: Partial<ReadableDb>): Promise<{ rows: PendingRow[]; errors: string[] }> {
  const rows: PendingRow[] = [];
  const errors: string[] = [];
  for (const spec of DRAIN_TABLES) {
    const table = dbLike[spec.table] as ReadableTable | undefined;
    if (!table) {
      errors.push(`${spec.table}: table missing`);
      continue;
    }
    let all: Array<Record<string, unknown>>;
    try {
      all = await table.toArray();
    } catch (err) {
      errors.push(`${spec.table}: ${err instanceof Error ? err.message : String(err)}`);
      continue;
    }
    all.forEach((row, index) => {
      if (spec.mode === 'flag' && !isPendingRow(row)) return;
      if (spec.mode === 'syncedAt' && row.synced_at !== undefined && row.synced_at !== null && row.synced_at !== '') return;
      const pk = primaryKeyOf(table, row) ?? `idx${index}`;
      rows.push({ spec, key: `${spec.table}:${pk}`, primaryKey: pk, row });
    });
  }
  return { rows, errors };
}
